import * as React from "react";
import { toast } from "react-toastify";
import { SetupIntent } from "@stripe/stripe-js";
import { useHistory } from "react-router-dom";

import { ITerminology } from "src/library/utils";
import { Text } from "src/library/components/Text";
import {
  PaymentMethodCardGridList,
  PaymentMethodCardListProps
} from "src/modules/payment/components/PaymentMethodCard";
import {
  IPaymentCard,
  usePaymentApi,
  PaymentCardUsage
} from "src/modules/payment/api/payment.api";
import { StripeElementsSetupCardModal } from "src/modules/payment/components";
import { paymentCardBrandTypeToHuman } from "src/modules/payment/utils";

interface PaymentMethodsSettingsTabProps {
  terminology: ITerminology
}

/**
 * Payment card settings tab
 */
const PaymentMethodsSettingsTab: React.ReactFCWithChildren<PaymentMethodsSettingsTabProps> = ({ terminology }) => {
  const history = useHistory();
  const [ paymentMethods, setPaymentMethods ] = React.useState<PaymentMethodCardListProps[]>();
  const [ showStripeElementsModal, setShowStripeElementsModal ] = React.useState(false);
  const [ listPaymentMethodsRes, listPaymentMethodsReq ] = usePaymentApi("LIST_PAYMENT_METHODS");
  const [ createPaymentMethodRes, createPaymentMethodReq ] = usePaymentApi("CREATE_PAYMENT_METHOD");

  /**
   * Handles the onSuccess callback when a new card is added
   */
  const handleAddCardSuccess = React.useCallback((setupIntent: SetupIntent, defaultCard: boolean) => {
    // Update the showStripeElementsModal state
    setShowStripeElementsModal(false);

    // Update the paymentMethods state, with a fake pending new card
    setPaymentMethods(currentPaymentMethods => {
      const newCard: PaymentMethodCardListProps = {
        loading: true,
        uuid: "pending-new-card"
      };

      // If the current state is undefined
      if (!currentPaymentMethods) {
        return [ newCard ];
      }

      // Prepend out card into the currentPaymentMethods array
      return [ newCard, ...currentPaymentMethods ];
    });

    // Attempt to create the payment method in our DB
    createPaymentMethodReq({
      externalId: setupIntent.payment_method as string,
      defaultCard
    });
  }, [ createPaymentMethodReq ]);

  // On mount
  React.useEffect(() => {
    // If there are no paymentMethods set, attempt to get them via the API
    if (!paymentMethods) {
      listPaymentMethodsReq({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On listPaymentMethodsRes change
  React.useEffect(() => {
    // If an error occurs
    if (listPaymentMethodsRes.error) {
      // Show the error message
      toast.error(listPaymentMethodsRes.error.message);
    }

    // On success
    if (listPaymentMethodsRes.data) {
      // Update the paymentMethods state
      const listPaymentMethodsResData = listPaymentMethodsRes.data;
      const parsedCards: PaymentMethodCardListProps[] = [];

      // Generate list of payment methods
      if (listPaymentMethodsResData.length > 0) {
        const parsedPaymentMethodCards = listPaymentMethodsResData.map(card => ({
          uuid: card.id,
          cardDetails: card,
          onClick: () => history.push(`/payment-methods/${card.id}`)
        }));

        parsedCards.push(...parsedPaymentMethodCards);
      }

      // Now append the "Add payment method" item to the end of the array
      parsedCards.push({
        uuid: "add-payment-method",
        defaultText: "Add payment method",
        onClick: () => setShowStripeElementsModal(true)
      });
      setPaymentMethods(parsedCards);
    }
  }, [
    listPaymentMethodsRes.data,
    listPaymentMethodsRes.error,
    history
  ]);

  // On createPaymentMethodRes change
  React.useEffect(() => {
    // If an error occurs
    if (createPaymentMethodRes.error) {
      // Show the error message
      toast.error(createPaymentMethodRes.error.message);

      // Update the paymentMethods state to remove the fake card
      setPaymentMethods(currentPaymentMethods => {
        // If the current state is undefined or if it is an empty array
        if (!currentPaymentMethods || currentPaymentMethods && currentPaymentMethods.length === 0) {
          return currentPaymentMethods;
        }

        // Remove the card from the top
        currentPaymentMethods.shift();

        return [ ...currentPaymentMethods ];
      });
    }

    // On success upload
    if (createPaymentMethodRes.data) {
      // Show a success message according to the card given usage
      if (createPaymentMethodRes.data.usage === PaymentCardUsage.OFF_SESSION) {
        toast.success(`Your ${paymentCardBrandTypeToHuman(createPaymentMethodRes.data.brand)} •••• ${createPaymentMethodRes.data.last4} has been added successfully.`);
      } else {
        toast.warn(`Your ${paymentCardBrandTypeToHuman(createPaymentMethodRes.data.brand)} •••• ${createPaymentMethodRes.data.last4} has been added successfully, however, it can only be used for on-session transactions set by the card issuer. You will need to manually authorize every transaction if you use this card to make an ${terminology.order.singular.text}.`, { autoClose: 18000 });
      }

      // Update the paymentMethods state with the official new card
      setPaymentMethods(currentPaymentMethods => {
        const card = createPaymentMethodRes.data as IPaymentCard;

        history.push(`/payment-methods/${card.id}`);

        const newCard: PaymentMethodCardListProps = {
          uuid: card.id,
          cardDetails: card,
          onClick: () => history.push(`/payment-methods/${card.id}`)
        };

        // If the current state is undefined
        if (!currentPaymentMethods) {
          return [ newCard ];
        }

        // Replace the top "pending-new-card" item with the official new card
        if (currentPaymentMethods[ 0 ] && currentPaymentMethods[ 0 ].uuid === "pending-new-card") {
          currentPaymentMethods[ 0 ] = newCard;
        }

        return [ ...currentPaymentMethods ];
      });
      // Take the use to the newly created card
      history.push(`/payment-methods/${createPaymentMethodRes.data.id}`);
    }
  // Ignore other dependencies, we only want this to update if createPaymentMethodRes changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ createPaymentMethodRes.data, createPaymentMethodRes.error ]);

  return (
    <>
      <Text>
        {`Add, manage and assign ${terminology.drop.plural.text} to payment methods.`}
      </Text>

      <PaymentMethodCardGridList
        loading={listPaymentMethodsRes.pending}
        cards={paymentMethods}
      />

      <StripeElementsSetupCardModal
        title="Add payment method"
        open={showStripeElementsModal}
        onDismiss={() => setShowStripeElementsModal(false)}
        onSetupIntentSuccess={handleAddCardSuccess}
      />
    </>
  );
};

export default PaymentMethodsSettingsTab;
